import { Formik } from "formik";
import { get } from "lodash";
import { useTranslation } from "react-i18next";
import {
  MdBackHand,
  MdCardGiftcard,
  MdInfo,
  MdLocationCity,
  MdLock,
  MdOutlineCall,
  MdOutlineLocationOn,
  MdOutlineMail
} from "react-icons/md";
import InfoBox from "screens/Private/Broker/Projects/Detail/Templates/Manual/Lafourgale/Components/InfoBox";
import Input from "shared/components/Input";
import Select from "shared/components/Select";
import useZipCodeCities from "shared/hooks/useZipCodeCities";
import { NAME_SPACES } from "shared/locales/constants";
import { CONTACT_POINT_SYSTEM } from "utils/constants";
import { bindInputProps } from "utils/helpers/input";
import ActionButtons from "../../../../../../Projects/Detail/Templates/Manual/Lafourgale/Components/ActionButtons";
import validation from "./validation";

const View = ({
  onSubmit,
  loading,
  setLoading,
  subscription,
  generalCost,
  initialValues,
  totalPrice,
}) => {
  const { cities, loading: citiesLoading } = useZipCodeCities(
    get(
      initialValues,
      `contactTelecoms.${CONTACT_POINT_SYSTEM.ZIP_CODE}.value`,
    ),
  );
  const { t } = useTranslation(NAME_SPACES.PRIVATE.BROKER.PROJECT);
  const TRANSLATION = t("DETAIL.TEMPLATES.LAFOURGALE.SUBSCRIPTION", {
    returnObjects: true,
  });

  return (
    <Formik
      validateOnMount={false}
      initialValues={initialValues}
      onSubmit={onSubmit}
      validationSchema={validation(
        t(`${NAME_SPACES.ERRORS}:VALIDATIONS`, { returnObjects: true }),
      )}
    >
      {({ values, handleSubmit, isValid, ...formProps }) => {
        return (
          <div className="lf-subscription" key="form_area">
            <div className="lf-subscription__container">
              <div className="lf-subscription__container--header">
                <strong>Souscription et Paiement</strong>
                <p>
                  Phase 5: Finalisation avec coordonnées bancaires et
                  consentements RGPD
                </p>
              </div>
              <div className="lf-subscription__container--content mb-32">
                <div className="lf-content__head">
                  <div className="lf-content__head--icon green">
                    <MdLocationCity size={22} />
                  </div>
                  <div className="lf-content__head--titles">
                    <h2 className="lf-content__head--titles__title">Adresse</h2>
                    <p className="lf-content__head--titles__subtitle">
                      Complétez vos informations d'adresse
                    </p>
                  </div>
                </div>
                <div
                  className="lf-subscription__container--content__form"
                  key="ADDRESS"
                >
                  <Input
                    theme="light"
                    icon={<MdOutlineLocationOn size={22} />}
                    label={TRANSLATION.ZIP_LABEL}
                    placeholder={TRANSLATION.ZIP_PLACEHOLDER}
                    {...bindInputProps({
                      name: `contactTelecoms.${CONTACT_POINT_SYSTEM.ZIP_CODE}.value`,
                      values,
                      ...formProps,
                    })}
                    disabled
                  />
                  <Select
                    theme="light"
                    showSearch
                    loading={citiesLoading}
                    options={cities}
                    icon={<MdOutlineLocationOn size={22} />}
                    label={TRANSLATION.CITY_LABEL}
                    placeholder={TRANSLATION.CITY_PLACEHOLDER}
                    {...bindInputProps({
                      name: `contactTelecoms.${CONTACT_POINT_SYSTEM.CITY}.value`,
                      values,
                      ...formProps,
                    })}
                  />
                  <Input
                    theme="light"
                    icon={<MdOutlineLocationOn size={22} />}
                    label={TRANSLATION.ADDRESS_LABEL}
                    placeholder={TRANSLATION.ADDRESS_PLACEHOLDER}
                    type="custom"
                    normalizeText={true}
                    {...bindInputProps({
                      name: `contactTelecoms.${CONTACT_POINT_SYSTEM.ADDRESS}.value`,
                      values,
                      ...formProps,
                    })}
                  />
                  <Input
                    theme="light"
                    icon={<MdOutlineMail size={22} />}
                    label={TRANSLATION.EMAIL_LABEL}
                    placeholder={TRANSLATION.EMAIL_PLACEHOLDER}
                    {...bindInputProps({
                      name: `contactTelecoms.${CONTACT_POINT_SYSTEM.EMAIL}.value`,
                      values,
                      ...formProps,
                    })}
                    disabled
                  />
                  <Input
                    theme="light"
                    icon={<MdOutlineCall size={22} />}
                    label={TRANSLATION.PHONE_LABEL}
                    placeholder={TRANSLATION.PHONE_PLACEHOLDER}
                    {...bindInputProps({
                      name: `contactTelecoms.${CONTACT_POINT_SYSTEM.PHONE}.value`,
                      values,
                      ...formProps,
                    })}
                    disabled
                  />
                </div>
              </div>
              <div className="lf-subscription__container--content mb-32">
                <div className="lf-content__head">
                  <div className="lf-content__head--icon blue">
                    <MdCardGiftcard size={22} />
                  </div>
                  <div className="lf-content__head--titles">
                    <h2 className="lf-content__head--titles__title">
                      Coordonnées bancaires
                    </h2>
                    <p className="lf-content__head--titles__subtitle">
                      Pour le prélèvement SEPA mensuel
                    </p>
                  </div>
                </div>
                <div
                  className="lf-subscription__container--content__form"
                  key="BANKING"
                >
                  <Input
                    theme="light"
                    icon={<MdLock size={22} />}
                    label={TRANSLATION.ACCOUNT_HOLDER_LABEL}
                    placeholder={TRANSLATION.ACCOUNT_HOLDER_PLACEHOLDER}
                    type="custom"
                    normalizeText={true}
                    {...bindInputProps({
                      name: `payment.payer.owner.firstName`,
                      values,
                      ...formProps,
                    })}
                  />
                  <Input
                    theme="light"
                    icon={<MdLock size={22} />}
                    label={TRANSLATION.ACCOUNT_HOLDER_LABEL}
                    placeholder={TRANSLATION.ACCOUNT_HOLDER_PLACEHOLDER}
                    type="custom"
                    normalizeText={true}
                    {...bindInputProps({
                      name: `payment.payer.owner.lastName`,
                      values,
                      ...formProps,
                    })}
                  />
                  <Input
                    theme="light"
                    icon={<MdLock size={22} />}
                    label={TRANSLATION.IBAN_LABEL}
                    placeholder={TRANSLATION.IBAN_PLACEHOLDER}
                    type="custom"
                    stripSpaces={true}
                    {...bindInputProps({
                      name: `payment.payer.IBAN`,
                      values,
                      ...formProps,
                    })}
                  />
                  <Input
                    theme="light"
                    icon={<MdLock size={22} />}
                    label={TRANSLATION.BIC_LABEL}
                    placeholder={TRANSLATION.BIC_PLACEHOLDER}
                    type="custom"
                    stripSpaces={true}
                    {...bindInputProps({
                      name: `payment.payer.BIC`,
                      values,
                      ...formProps,
                    })}
                  />

                  <InfoBox
                    variant="info"
                    icon={<MdInfo size={22} />}
                    content={
                      <>
                        <strong>Sécurité et confidentialité</strong>
                        <p>
                          Vos coordonnées bancaires sont chiffrées et
                          sécurisées. Le prélèvement sera effectué mensuellement
                          pour un montant de <b>{totalPrice.toFixed(2)} €</b>.
                        </p>
                      </>
                    }
                  />
                </div>
              </div>
              <div className="lf-subscription__container--content mb-32">
                <div className="lf-content__head">
                  <div className="lf-content__head--icon green">
                    <MdBackHand size={22} />
                  </div>
                  <div className="lf-content__head--titles">
                    <h2 className="lf-content__head--titles__title">
                      Consentements et préférences
                    </h2>
                    <p className="lf-content__head--titles__subtitle">
                      Conformité RGPD
                    </p>
                  </div>
                </div>
                <div
                  className="lf-subscription__container--content__form"
                  key="CONSENT"
                >
                  <Input
                    type="checkbox"
                    label={TRANSLATION.OPTIN_MARKETING}
                    defaultChecked={get(
                      values,
                      "consent.optinMarketing",
                      false,
                    )}
                    {...bindInputProps({
                      name: `consent.optinMarketing`,
                      values,
                      ...formProps,
                    })}
                    required
                  />
                  <Input
                    type="checkbox"
                    label={TRANSLATION.OPTIN_PARTNERS}
                    defaultChecked={get(values, "consent.optinPartners", false)}
                    {...bindInputProps({
                      name: `consent.optinPartners`,
                      values,
                      ...formProps,
                    })}
                    required
                  />
                </div>
              </div>
              <div className="lf-subscription__container--content mb-32">
                <div className="lf-content__head">
                  <div className="lf-content__head--titles">
                    <h2 className="lf-content__head--titles__title">
                      Acceptation des conditions
                    </h2>
                  </div>
                </div>
                <div
                  className="lf-subscription__container--content__form"
                  key="ACCEPT_CONDITIONS"
                >
                  <Input
                    type="checkbox"
                    label={TRANSLATION.ACCEPT_CGV}
                    defaultChecked={get(values, "consent.acceptCGV", false)}
                    {...bindInputProps({
                      name: `consent.acceptCGV`,
                      values,
                      ...formProps,
                    })}
                    required
                  />
                  <Input
                    type="checkbox"
                    label={TRANSLATION.ACCEPT_SEPA}
                    defaultChecked={get(values, "consent.acceptSepa", false)}
                    {...bindInputProps({
                      name: `consent.acceptSepa`,
                      values,
                      ...formProps,
                    })}
                    required
                  />
                </div>
              </div>
              <InfoBox
                variant="info"
                content={
                  <div className="lf-subscription__summary">
                    <div className="lf-subscription__summary--title">
                      <strong>
                        {TRANSLATION.SUBSCRIPTION_SUMMARY_LABEL}:{" "}
                      </strong>
                    </div>
                    <div className="lf-subscription__summary--line">
                      <div>Formule téléconsultation:</div>
                      <strong>{get(subscription, "insurancePlan.name")}</strong>
                    </div>
                    {get(
                      subscription,
                      `project.fields.upsells.${get(subscription, "insurancePlan.id")}`,
                      [],
                    ).map((_) => (
                      <div
                        key={_.id}
                        className="lf-subscription__summary--line"
                      >
                        <div>Formule Cashback:</div>
                        <strong>{get(_, "insurancePlan.name")}</strong>
                      </div>
                    ))}
                    <div className="lf-subscription__summary--recap">
                      <div>Montant mensuel:</div>
                      <strong>{totalPrice.toFixed(2)} €/mois</strong>
                    </div>
                  </div>
                }
              />
              <div className="lf-subscription__container--content mb-32"></div>
              <ActionButtons
                onSubmit={handleSubmit}
                loading={loading}
                translation={TRANSLATION}
                disabled={{ next: !isValid }}
              />
            </div>
          </div>
        );
      }}
    </Formik>
  );
};

export default View;
